import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { ColumnsType } from 'antd/es/table';
import {
  WxbAlert,
  WxbButton,
  WxbDataTable,
  WxbDrawer,
  WxbEmpty,
  WxbModal,
  WxbPageHeader,
  WxbPageShell,
  WxbPopconfirm,
  WxbSearchInput,
  WxbSegmented,
  WxbSelect,
  WxbSpinner,
  WxbStatusBadge,
  WxbTag,
  WxbTree,
  WxbValidationList,
  wxbToast,
} from '../components/wxb-ui';
import ResourceRelationshipGraph from '../components/ProdResourceTopology/ResourceRelationshipGraph';
import ResourceTopologyImportModal from '../components/ProdResourceTopology/ResourceTopologyImportModal';
import { downloadResourceTopologyImportWorkbook } from '../components/ProdResourceTopology/resourceTopologyImport';
import ResourceTopologyInspector, {
  type ResourceTopologySelection,
} from '../components/ProdResourceTopology/ResourceTopologyInspector';
import {
  buildResourceForest,
  canPublishVersion,
  findActiveTopologyDraft,
  formatRelationshipStatement,
  isWritableVersion,
  parseFacilityCodes,
  RELATIONSHIP_TYPE_LABELS,
  relationsForResource,
  RESOURCE_KIND_LABELS,
} from '../components/ProdResourceTopology/resourceTopologyModel';
import { useAuth } from '../contexts/AuthContext';
import {
  normalizeResourceTopologyError,
  prodResourceTopologyApi,
} from '../services/prodResourceTopologyApi';
import type {
  ResourceNode,
  ResourceNodeDraft,
  ResourceRelationship,
  ResourceRelationshipDraft,
  ResourceTopologyConflict,
  ResourceTopologyHistoryItem,
  ResourceTopologyIssue,
  ResourceTopologyWorkspace,
  ResourceTopologyVersion,
} from '../types/prodResourceTopology';
import './ProdCipTopologyPage.css';

const VERSION_STATUS_LABELS: Record<ResourceTopologyVersion['status'], string> = {
  DRAFT: '草稿',
  VALIDATED: '已校验',
  PUBLISHED: '已发布',
  SUPERSEDED: '已取代',
  ABANDONED: '已放弃',
};

const FACILITY_CODES = parseFacilityCodes(process.env.REACT_APP_PROD_RESOURCE_FACILITY_CODES);

const versionStatusTone = (status: ResourceTopologyVersion['status']) => {
  if (status === 'PUBLISHED') return 'success' as const;
  if (status === 'VALIDATED') return 'info' as const;
  if (status === 'DRAFT') return 'warning' as const;
  return 'neutral' as const;
};

const lifecycleLabel = (node: ResourceNode): string => {
  if (node.lifecycleStatus === 'ACTIVE') return '启用';
  if (node.lifecycleStatus === 'INACTIVE') return '停用';
  return '已退役';
};

const formatTime = (value: string | null): string => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString('zh-CN', { hour12: false });
};

const ProdCipTopologyPage: React.FC = () => {
  const { user, hasPermission } = useAuth();
  const canWrite = !user || hasPermission('MASTER_RESOURCE_WRITE');
  const canOperate = !user || hasPermission('MASTER_RESOURCE_OPERATE');
  const [facilityCode, setFacilityCode] = useState(FACILITY_CODES[0]);
  const [versions, setVersions] = useState<ResourceTopologyHistoryItem[]>([]);
  const [workspace, setWorkspace] = useState<ResourceTopologyWorkspace | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [validating, setValidating] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [requestError, setRequestError] = useState<string | null>(null);
  const [conflict, setConflict] = useState<ResourceTopologyConflict | null>(null);
  const [search, setSearch] = useState('');
  const [relationshipView, setRelationshipView] = useState<'graph' | 'table'>('graph');
  const [focusedResourceId, setFocusedResourceId] = useState<string | null>(null);
  const [selection, setSelection] = useState<ResourceTopologySelection>(null);
  const [inspectorDirty, setInspectorDirty] = useState(false);
  const [pendingSelection, setPendingSelection] = useState<ResourceTopologySelection>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [issuesExpanded, setIssuesExpanded] = useState(true);
  const activeDraft = findActiveTopologyDraft(versions);

  const loadWorkspace = useCallback(async (
    versionId: string,
    options?: { preserveSelection?: boolean },
  ) => {
    const next = await prodResourceTopologyApi.getWorkspace(versionId);
    setWorkspace(next);
    setFocusedResourceId((current) => {
      if (options?.preserveSelection && current && next.resources.some((node) => node.id === current)) return current;
      return next.resources[0]?.id ?? null;
    });
    if (!options?.preserveSelection) {
      setSelection(next.resources[0] ? { kind: 'node', id: next.resources[0].id } : null);
    }
    return next;
  }, []);

  const loadVersions = useCallback(async (preferredVersionId?: string) => {
    setLoading(true);
    setLoadError(null);
    try {
      const history = await prodResourceTopologyApi.listVersions(facilityCode);
      setVersions(history);
      const preferred = preferredVersionId
        ? history.find((version) => version.id === preferredVersionId)
        : null;
      const selected = preferred
        ?? history.find((version) => version.status === 'DRAFT' || version.status === 'VALIDATED')
        ?? history.find((version) => version.status === 'PUBLISHED')
        ?? history[0]
        ?? null;
      if (selected) {
        await loadWorkspace(selected.id);
      } else {
        setWorkspace(null);
        setFocusedResourceId(null);
        setSelection(null);
      }
    } catch (error) {
      const normalized = normalizeResourceTopologyError(error);
      setLoadError(normalized.message);
    } finally {
      setLoading(false);
    }
  }, [facilityCode, loadWorkspace]);

  useEffect(() => {
    loadVersions();
  }, [loadVersions]);

  const updateVersionInHistory = useCallback((version: ResourceTopologyVersion) => {
    setVersions((current) => current.map((item) => (
      item.id === version.id ? { ...item, ...version } : item
    )));
  }, []);

  const handleFailure = useCallback((error: unknown): never => {
    const normalized = normalizeResourceTopologyError(error);
    setRequestError(normalized.message);
    if (normalized.kind === 'revision-conflict') {
      setConflict(normalized);
    } else if (normalized.issues.length > 0) {
      setWorkspace((current) => current ? { ...current, issues: normalized.issues } : current);
      setIssuesExpanded(true);
    }
    throw error;
  }, []);

  const refreshCurrentWorkspace = useCallback(async (preserveSelection = true) => {
    if (!workspace) return;
    const next = await loadWorkspace(workspace.version.id, { preserveSelection });
    updateVersionInHistory(next.version);
  }, [loadWorkspace, updateVersionInHistory, workspace]);

  const selectResource = useCallback((resourceId: string) => {
    const next: ResourceTopologySelection = { kind: 'node', id: resourceId };
    if (inspectorDirty) {
      setPendingSelection(next);
      return;
    }
    setFocusedResourceId(resourceId);
    setSelection(next);
    setConflict(null);
    setRequestError(null);
  }, [inspectorDirty]);

  const selectRelationship = useCallback((relationshipId: string) => {
    const next: ResourceTopologySelection = { kind: 'relationship', id: relationshipId };
    if (inspectorDirty) {
      setPendingSelection(next);
      return;
    }
    setSelection(next);
    setConflict(null);
    setRequestError(null);
  }, [inspectorDirty]);

  const selectCreation = useCallback((kind: 'new-node' | 'new-relationship') => {
    const next: ResourceTopologySelection = { kind };
    if (inspectorDirty) {
      setPendingSelection(next);
      return;
    }
    setSelection(next);
    setConflict(null);
    setRequestError(null);
  }, [inspectorDirty]);

  const confirmDiscard = () => {
    const next = pendingSelection;
    setInspectorDirty(false);
    setPendingSelection(null);
    if (!next) return;
    if (next.kind === 'node') setFocusedResourceId(next.id);
    setSelection(next);
    setConflict(null);
    setRequestError(null);
  };

  const createDraft = async () => {
    if (!canWrite) return;
    setSaving(true);
    setRequestError(null);
    try {
      if (activeDraft) {
        await loadWorkspace(activeDraft.id);
        wxbToast.info('已返回当前活动草稿');
        return;
      }
      const baseVersion = workspace
        && (workspace.version.status === 'PUBLISHED' || workspace.version.status === 'SUPERSEDED')
        ? workspace.version
        : versions.find((version) => version.status === 'PUBLISHED') ?? null;
      const version = await prodResourceTopologyApi.createDraft({
        facilityCode,
        baseVersionId: baseVersion?.id,
      });
      await loadVersions(version.id);
      wxbToast.success('资源拓扑草稿已创建');
    } catch (error) {
      try { handleFailure(error); } catch { /* feedback stored */ }
    } finally {
      setSaving(false);
    }
  };

  const abandonDraft = async () => {
    if (!workspace || !isWritableVersion(workspace.version) || !canOperate) return;
    setSaving(true);
    setRequestError(null);
    setConflict(null);
    try {
      await prodResourceTopologyApi.abandon(workspace.version.id, workspace.version.revision);
      await loadVersions();
      wxbToast.success('资源拓扑草稿已放弃');
    } catch (error) {
      try { handleFailure(error); } catch { /* feedback stored */ }
    } finally {
      setSaving(false);
    }
  };

  const saveNode = async (
    draft: ResourceNodeDraft,
    parentResourceId: string | null,
    existing: ResourceNode | null,
  ) => {
    if (!workspace) return;
    setSaving(true);
    setRequestError(null);
    setConflict(null);
    try {
      let resourceId = existing?.id ?? null;
      if (existing) {
        await prodResourceTopologyApi.updateNode(
          workspace.version.id,
          existing.id,
          workspace.version.revision,
          {
            name: draft.name,
            lifecycleStatus: draft.lifecycleStatus,
            attributes: draft.attributes,
            note: draft.note,
            parentResourceId,
          },
        );
      } else {
        const result = await prodResourceTopologyApi.createNode(
          workspace.version.id,
          workspace.version.revision,
          draft,
          parentResourceId,
        );
        resourceId = result.resource?.id ?? null;
      }

      if (!resourceId) throw new Error('服务端未返回资源节点标识');
      const refreshed = await loadWorkspace(workspace.version.id, { preserveSelection: true });
      updateVersionInHistory(refreshed.version);
      setFocusedResourceId(resourceId);
      setSelection({ kind: 'node', id: resourceId });
      setConflict(null);
      wxbToast.success(existing ? '资源节点已更新' : '资源节点已创建');
    } catch (error) {
      handleFailure(error);
    } finally {
      setSaving(false);
    }
  };

  const retireNode = async (node: ResourceNode) => {
    if (!workspace) return;
    setSaving(true);
    setRequestError(null);
    try {
      await prodResourceTopologyApi.retireNode(workspace.version.id, node.id, workspace.version.revision);
      const refreshed = await loadWorkspace(workspace.version.id, { preserveSelection: true });
      updateVersionInHistory(refreshed.version);
      wxbToast.success('资源节点已退役');
    } catch (error) {
      try { handleFailure(error); } catch { /* feedback stored */ }
    } finally {
      setSaving(false);
    }
  };

  const saveRelationship = async (
    draft: ResourceRelationshipDraft,
    existing: ResourceRelationship | null,
  ) => {
    if (!workspace) return;
    setSaving(true);
    setRequestError(null);
    setConflict(null);
    try {
      let relationshipId = existing?.id ?? null;
      if (existing) {
        await prodResourceTopologyApi.updateRelation(
          workspace.version.id,
          existing.id,
          workspace.version.revision,
          { priority: draft.priority, note: draft.note },
        );
      } else {
        const result = await prodResourceTopologyApi.createRelation(
          workspace.version.id,
          workspace.version.revision,
          draft,
        );
        relationshipId = result.relationship?.id ?? null;
      }
      const refreshed = await loadWorkspace(workspace.version.id, { preserveSelection: true });
      updateVersionInHistory(refreshed.version);
      if (relationshipId) setSelection({ kind: 'relationship', id: relationshipId });
      setConflict(null);
      wxbToast.success(existing ? '资源关系已更新' : '资源关系已创建');
    } catch (error) {
      handleFailure(error);
    } finally {
      setSaving(false);
    }
  };

  const deleteRelationship = async (relationship: ResourceRelationship) => {
    if (!workspace) return;
    setSaving(true);
    setRequestError(null);
    try {
      await prodResourceTopologyApi.deleteRelation(
        workspace.version.id,
        relationship.id,
        workspace.version.revision,
      );
      const refreshed = await loadWorkspace(workspace.version.id, { preserveSelection: true });
      updateVersionInHistory(refreshed.version);
      setSelection(focusedResourceId ? { kind: 'node', id: focusedResourceId } : null);
      wxbToast.success('资源关系已移除');
    } catch (error) {
      try { handleFailure(error); } catch { /* feedback stored */ }
    } finally {
      setSaving(false);
    }
  };

  const validateTopology = async () => {
    if (!workspace || !canOperate) return;
    setValidating(true);
    setRequestError(null);
    setConflict(null);
    try {
      const result = await prodResourceTopologyApi.validate(
        workspace.version.id,
        workspace.version.revision,
      );
      setWorkspace((current) => current ? {
        ...current,
        version: result.version,
        issues: result.issues,
      } : current);
      updateVersionInHistory(result.version);
      setIssuesExpanded(true);
      const hardCount = result.issues.filter((issue) => issue.hardOrSoft === 'hard').length;
      if (hardCount === 0) wxbToast.success('当前资源节点和关系已通过校验');
    } catch (error) {
      try { handleFailure(error); } catch { /* feedback stored */ }
    } finally {
      setValidating(false);
    }
  };

  const publishTopology = async () => {
    if (!workspace || !canOperate) return;
    setPublishing(true);
    setRequestError(null);
    try {
      const version = await prodResourceTopologyApi.publish(
        workspace.version.id,
        workspace.version.revision,
      );
      setPublishOpen(false);
      await loadVersions(version.id);
      wxbToast.success('资源节点和关系版本已发布');
    } catch (error) {
      try { handleFailure(error); } catch { /* feedback stored */ }
    } finally {
      setPublishing(false);
    }
  };

  const chooseVersion = async (versionId: string) => {
    setLoading(true);
    setLoadError(null);
    try {
      await loadWorkspace(versionId);
      setHistoryOpen(false);
    } catch (error) {
      setLoadError(normalizeResourceTopologyError(error).message);
    } finally {
      setLoading(false);
    }
  };

  const exportTopologyWorkbook = async () => {
    if (!workspace) return;
    try {
      await downloadResourceTopologyImportWorkbook(
        {
          resources: workspace.resources,
          relationships: workspace.relationships,
          mode: 'REPLACE',
          scope: 'FULL',
        },
        `${workspace.version.facilityCode}-资源拓扑-V${workspace.version.versionNo}-可覆盖导出.xlsx`,
      );
      wxbToast.success('已导出可覆盖的受控工作簿');
    } catch (error) {
      const message = error instanceof Error ? error.message : '导出受控工作簿失败';
      setRequestError(message);
    }
  };

  const locateIssue = (issue: ResourceTopologyIssue) => {
    const resourceId = issue.affectedEntities.find((entity) => entity.entityType === 'RESOURCE')?.entityId;
    if (resourceId && workspace?.resources.some((resource) => resource.id === resourceId)) {
      selectResource(resourceId);
      return;
    }
    const relationshipId = issue.affectedEntities.find((entity) => entity.entityType === 'RELATION')?.entityId;
    const relationship = relationshipId
      ? workspace?.relationships.find((item) => item.id === relationshipId)
      : null;
    if (relationship) {
      setFocusedResourceId(relationship.sourceResourceId);
      selectRelationship(relationship.id);
    }
  };

  const treeItems = useMemo(
    () => workspace ? buildResourceForest(workspace.resources, workspace.relationships, search) : [],
    [search, workspace],
  );
  const treeData = useMemo(() => {
    const mapItem = (item: typeof treeItems[number]): any => ({
      key: item.key,
      title: (
        <span className="resource-topology-tree-title">
          <WxbTag color={item.resource.kind === 'CIP_LOOP' ? 'cyan' : item.resource.kind === 'CIP_STATION' ? 'amber' : 'neutral'}>
            {RESOURCE_KIND_LABELS[item.resource.kind]}
          </WxbTag>
          <span className="resource-topology-tree-code">{item.resource.code}</span>
          <span className="resource-topology-tree-name">{item.resource.name}</span>
          {item.resource.lifecycleStatus !== 'ACTIVE' && (
            <WxbTag color={item.resource.lifecycleStatus === 'RETIRED' ? 'red' : 'neutral'}>
              {lifecycleLabel(item.resource)}
            </WxbTag>
          )}
        </span>
      ),
      children: item.children.map(mapItem),
    });
    return treeItems.map(mapItem);
  }, [treeItems]);

  const resourceById = useMemo(
    () => new Map(workspace?.resources.map((resource) => [resource.id, resource]) ?? []),
    [workspace?.resources],
  );
  const visibleRelationships = useMemo(
    () => relationsForResource(workspace?.relationships ?? [], focusedResourceId),
    [focusedResourceId, workspace?.relationships],
  );
  const relationshipColumns: ColumnsType<ResourceRelationship> = [
    {
      title: '关系说明',
      key: 'statement',
      render: (_, relationship) => formatRelationshipStatement(relationship, resourceById),
    },
  ];

  const writable = isWritableVersion(workspace?.version ?? null);
  const publishAllowed = canPublishVersion(workspace?.version ?? null, workspace?.issues ?? []);
  const hardIssueCount = workspace?.issues.filter((issue) => issue.hardOrSoft === 'hard').length ?? 0;
  const warningIssueCount = workspace?.issues.filter((issue) => issue.hardOrSoft === 'soft').length ?? 0;
  const historyColumns: ColumnsType<ResourceTopologyHistoryItem> = [
    { title: '版本', dataIndex: 'versionNo', width: 80, render: (value: number) => `V${value}` },
    { title: '名称', dataIndex: 'name' },
    {
      title: '状态',
      dataIndex: 'status',
      width: 110,
      render: (status: ResourceTopologyVersion['status']) => (
        <WxbStatusBadge tone={versionStatusTone(status)} label={VERSION_STATUS_LABELS[status]} size="sm" />
      ),
    },
    { title: 'revision', dataIndex: 'revision', width: 90 },
    { title: '发布时间', dataIndex: 'publishedAt', width: 170, render: formatTime },
    {
      title: '操作',
      key: 'action',
      width: 90,
      render: (_, row) => (
        <WxbButton type="button" variant="ghost" size="sm" onClick={() => chooseVersion(row.id)}>
          查看
        </WxbButton>
      ),
    },
  ];

  return (
    <WxbPageShell size="full" gap="sm" className="resource-topology-page">
      <WxbPageHeader
        eyebrow="生产排产数据底座"
        title="资源与物理拓扑"
        description="维护资源身份、节点属性和物理父子关系；CIP 逻辑回路在独立页面维护。"
        meta={workspace ? (
          <>
            <WxbStatusBadge
              tone={versionStatusTone(workspace.version.status)}
              label={VERSION_STATUS_LABELS[workspace.version.status]}
              size="sm"
            />
            <WxbTag color="neutral">revision {workspace.version.revision}</WxbTag>
          </>
        ) : undefined}
        actions={(
          <div className="resource-topology-header-actions">
            <WxbButton
              type="button"
              variant="ghost"
              disabled={inspectorDirty}
              onClick={() => setHistoryOpen(true)}
              title={inspectorDirty ? '请先保存或取消属性修改' : undefined}
            >
              版本历史
            </WxbButton>
            <WxbButton
              type="button"
              variant="ghost"
              disabled={!workspace || inspectorDirty}
              title={inspectorDirty
                ? '请先保存或取消属性修改，避免导出旧数据'
                : !workspace ? '暂无可导出的资源拓扑版本' : undefined}
              onClick={exportTopologyWorkbook}
            >
              导出
            </WxbButton>
            {workspace && writable ? (
              <>
                <WxbButton
                  type="button"
                  variant="secondary"
                  disabled={!canWrite || saving || inspectorDirty}
                  title={!canWrite
                    ? '当前账号仅有查看权限'
                    : inspectorDirty ? '请先保存或取消属性修改' : undefined}
                  onClick={() => setImportOpen(true)}
                >
                  导入数据
                </WxbButton>
                <WxbPopconfirm
                  title={`放弃草稿 V${workspace.version.versionNo}？`}
                  description="放弃后该版本只读；节点和关系不会发布。"
                  okText="确认放弃"
                  cancelText="取消"
                  disabled={!canOperate || saving || inspectorDirty}
                  onConfirm={abandonDraft}
                >
                  <WxbButton
                    type="button"
                    variant="danger"
                    disabled={!canOperate || saving || inspectorDirty}
                  >
                    放弃草稿
                  </WxbButton>
                </WxbPopconfirm>
                <WxbButton
                  type="button"
                  variant="secondary"
                  disabled={!canOperate || validating || saving || inspectorDirty}
                  onClick={validateTopology}
                >
                  {validating ? '校验中…' : '校验草稿'}
                </WxbButton>
                <WxbButton
                  type="button"
                  disabled={!canOperate || !publishAllowed || publishing || inspectorDirty}
                  onClick={() => setPublishOpen(true)}
                  title={!publishAllowed ? '当前 revision 必须先通过硬约束校验' : undefined}
                >
                  发布
                </WxbButton>
              </>
            ) : activeDraft ? (
              <WxbButton
                type="button"
                disabled={loading || inspectorDirty}
                onClick={() => chooseVersion(activeDraft.id)}
              >
                返回当前草稿
              </WxbButton>
            ) : (
              <WxbButton type="button" disabled={!canWrite || saving} onClick={createDraft}>
                {saving ? '创建中…' : '新建草稿'}
              </WxbButton>
            )}
          </div>
        )}
      />

      <div className="resource-topology-context-bar">
        <WxbSelect
          label="设施"
          className="resource-topology-facility-select"
          value={facilityCode}
          options={FACILITY_CODES.map((code) => ({ label: code, value: code }))}
          onChange={(value) => setFacilityCode(value)}
          disabled={inspectorDirty}
        />
        <WxbSelect
          label="当前版本"
          className="resource-topology-version-select"
          value={workspace?.version.id}
          options={versions.map((version) => ({
            label: `V${version.versionNo} · ${version.name} · ${VERSION_STATUS_LABELS[version.status]}`,
            value: version.id,
          }))}
          onChange={chooseVersion}
          disabled={loading || versions.length === 0 || inspectorDirty}
        />
        {workspace && !writable && (
          <span className="resource-topology-readonly-note">当前版本只读；创建草稿后才能维护节点和关系。</span>
        )}
        {workspace && writable && !canWrite && (
          <span className="resource-topology-readonly-note">当前账号仅有查看权限。</span>
        )}
      </div>

      {requestError && (
        <WxbAlert variant="error" title="操作未完成">
          <span>{requestError}</span>
          <WxbButton type="button" variant="ghost" size="sm" onClick={() => setRequestError(null)}>
            关闭提示
          </WxbButton>
        </WxbAlert>
      )}

      {loading ? (
        <div className="resource-topology-loading">
          <WxbSpinner tip="加载资源节点与关系…" />
        </div>
      ) : loadError ? (
        <WxbAlert variant="error" title="资源拓扑加载失败">
          <p>{loadError}</p>
          <WxbButton type="button" variant="secondary" onClick={() => loadVersions(workspace?.version.id)}>
            重试
          </WxbButton>
        </WxbAlert>
      ) : !workspace ? (
        <WxbEmpty
          description="当前设施还没有资源拓扑版本"
          action={canWrite ? (
            <WxbButton type="button" onClick={createDraft} disabled={saving}>
              创建第一个草稿
            </WxbButton>
          ) : undefined}
        />
      ) : (
        <>
          <main className="resource-topology-workspace">
            <section className="resource-topology-panel resource-topology-tree-panel" aria-labelledby="resource-tree-title">
              <header className="resource-topology-panel-header">
                <div>
                  <h2 id="resource-tree-title">资源节点</h2>
                  <span>{workspace.resources.length} 个节点</span>
                </div>
                <WxbButton
                  type="button"
                  size="sm"
                  disabled={!writable || !canWrite}
                  onClick={() => selectCreation('new-node')}
                >
                  新建节点
                </WxbButton>
              </header>
              <div className="resource-topology-tree-search">
                <WxbSearchInput
                  value={search}
                  onChange={setSearch}
                  placeholder="搜索编码、名称或类型"
                  aria-label="搜索资源节点"
                />
              </div>
              <div className="resource-topology-tree-body">
                {treeData.length > 0 ? (
                  <WxbTree
                    treeData={treeData}
                    selectedKeys={focusedResourceId ? [focusedResourceId] : []}
                    onSelect={(keys) => keys[0] && selectResource(String(keys[0]))}
                    defaultExpandAll
                    showLine
                    blockNode
                  />
                ) : (
                  <WxbEmpty
                    description={search ? '没有符合搜索条件的资源节点' : '草稿中还没有资源节点'}
                    action={search ? (
                      <WxbButton type="button" variant="ghost" size="sm" onClick={() => setSearch('')}>
                        清除搜索
                      </WxbButton>
                    ) : undefined}
                  />
                )}
              </div>
            </section>

            <section className="resource-topology-panel resource-topology-relations-panel" aria-labelledby="resource-relations-title">
              <header className="resource-topology-panel-header">
                <div>
                  <h2 id="resource-relations-title">资源关系</h2>
                  <span>{focusedResourceId ? '显示所选节点的相邻关系' : '显示全部关系'}</span>
                </div>
                <div className="resource-topology-panel-actions">
                  <WxbSegmented
                    size="sm"
                    value={relationshipView}
                    options={[
                      { label: '关系图', value: 'graph' },
                      { label: '关系表', value: 'table' },
                    ]}
                    onChange={(value) => setRelationshipView(value as 'graph' | 'table')}
                  />
                  <WxbButton
                    type="button"
                    size="sm"
                    disabled={!writable || !canWrite}
                    onClick={() => selectCreation('new-relationship')}
                  >
                    新建关系
                  </WxbButton>
                </div>
              </header>
              <div className="resource-topology-relations-body">
                {relationshipView === 'graph' ? (
                  <ResourceRelationshipGraph
                    resources={workspace.resources}
                    relationships={workspace.relationships}
                    selectedResourceId={focusedResourceId}
                    selectedRelationshipId={selection?.kind === 'relationship' ? selection.id : null}
                    onSelectResource={selectResource}
                    onSelectRelationship={selectRelationship}
                  />
                ) : (
                  <WxbDataTable<ResourceRelationship>
                    rowKey="id"
                    density="compact"
                    columns={relationshipColumns}
                    dataSource={visibleRelationships}
                    pagination={false}
                    scroll={{ x: 680 }}
                    rowClassName={(row) => selection?.kind === 'relationship' && selection.id === row.id ? 'is-selected' : ''}
                    onRow={(row) => ({
                      onClick: () => selectRelationship(row.id),
                      onKeyDown: (event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          selectRelationship(row.id);
                        }
                      },
                      tabIndex: 0,
                      'aria-label': `查看${RELATIONSHIP_TYPE_LABELS[row.type]}关系`,
                    })}
                    emptyState={{ description: '所选节点还没有资源关系' }}
                  />
                )}
              </div>
            </section>

            <ResourceTopologyInspector
              selection={selection}
              resources={workspace.resources}
              relationships={workspace.relationships}
              writable={writable}
              canWrite={canWrite}
              saving={saving}
              conflict={conflict}
              onReloadConflict={async () => {
                await refreshCurrentWorkspace(true);
                setConflict(null);
              }}
              onSaveNode={saveNode}
              onRetireNode={retireNode}
              onSaveRelationship={saveRelationship}
              onDeleteRelationship={deleteRelationship}
              onDirtyChange={setInspectorDirty}
            />
          </main>

          <section className="resource-topology-validation" aria-labelledby="resource-validation-title">
            <header className="resource-topology-validation-header">
              <div>
                <h2 id="resource-validation-title">维护保护</h2>
                <span>
                  {workspace.issues.length > 0
                    ? `${hardIssueCount} 个硬错误，${warningIssueCount} 个提示`
                    : workspace.version.status === 'VALIDATED'
                      ? `revision ${workspace.version.revision} 已通过校验`
                      : '当前 revision 尚未校验'}
                </span>
              </div>
              <WxbButton type="button" variant="ghost" size="sm" onClick={() => setIssuesExpanded((value) => !value)}>
                {issuesExpanded ? '收起' : '展开'}
              </WxbButton>
            </header>
            {issuesExpanded && (
              workspace.issues.length > 0 ? (
                <WxbValidationList
                  density="compact"
                  items={workspace.issues.map((issue) => ({
                    key: issue.id,
                    level: issue.hardOrSoft === 'hard' ? 'error' : issue.severity === 'warning' ? 'warning' : 'info',
                    title: issue.constraintCode,
                    description: issue.suggestedAction
                      ? `${issue.violationReason}；建议：${issue.suggestedAction}`
                      : issue.violationReason,
                    source: issue.hardOrSoft === 'hard' ? '硬约束' : '软约束',
                    action: issue.affectedEntities.some((entity) => entity.entityType === 'RESOURCE' || entity.entityType === 'RELATION') ? (
                      <WxbButton type="button" variant="ghost" size="sm" onClick={() => locateIssue(issue)}>
                        定位节点
                      </WxbButton>
                    ) : undefined,
                  }))}
                />
              ) : workspace.version.status === 'VALIDATED' ? (
                <div className="resource-topology-validation-ok">
                  <WxbStatusBadge tone="success" label="当前节点和关系已通过校验" />
                </div>
              ) : (
                <WxbAlert title="发布前需要校验当前 revision">
                  校验只检查节点和关系的结构完整性；不会在此页面执行排产或运行态检查。
                </WxbAlert>
              )
            )}
          </section>
        </>
      )}

      <WxbDrawer
        title="资源拓扑版本历史"
        open={historyOpen}
        width={720}
        onClose={() => setHistoryOpen(false)}
      >
        <WxbDataTable<ResourceTopologyHistoryItem>
          rowKey="id"
          density="compact"
          columns={historyColumns}
          dataSource={versions}
          pagination={false}
          emptyState={{ description: '当前设施还没有版本记录' }}
        />
      </WxbDrawer>

      {workspace && (
        <ResourceTopologyImportModal
          key={workspace.version.id}
          open={importOpen}
          versionId={workspace.version.id}
          revision={workspace.version.revision}
          canWrite={canWrite && writable}
          onClose={() => setImportOpen(false)}
          onRefreshRevision={() => refreshCurrentWorkspace(true)}
          onCommitted={async (result) => {
            const refreshed = await loadWorkspace(result.version.id, {
              preserveSelection: Boolean(focusedResourceId),
            });
            updateVersionInHistory(refreshed.version);
            setImportOpen(false);
            wxbToast.success(
              `导入完成：新增 ${result.summary.createNodes + result.summary.createRelationships} 项，覆盖 ${result.summary.updateNodes + result.summary.updateRelationships} 项`,
            );
          }}
        />
      )}

      <WxbModal
        title="发布资源节点和关系版本"
        open={publishOpen}
        okText="确认发布"
        cancelText="取消"
        confirmLoading={publishing}
        onOk={publishTopology}
        onCancel={() => setPublishOpen(false)}
      >
        <p>
          将发布 V{workspace?.version.versionNo} 的 revision {workspace?.version.revision}。发布后该版本只读，
          后续维护需要从已发布版本创建新草稿。
        </p>
      </WxbModal>

      <WxbModal
        title="放弃未保存的修改？"
        open={Boolean(pendingSelection)}
        okText="放弃并切换"
        cancelText="继续编辑"
        okVariant="danger"
        onOk={confirmDiscard}
        onCancel={() => setPendingSelection(null)}
      >
        <p>属性检查器中有未保存内容。切换节点或关系会丢失这些输入。</p>
      </WxbModal>
    </WxbPageShell>
  );
};

export default ProdCipTopologyPage;
